
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { accountService, landlordApi } from '../../../services/api';

const getTenantId = (): string | null => {
    try {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('idToken');
        if (!token) return null;
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload['custom:tenantId'] || null;
    } catch {
        return null;
    }
};

interface ProfileData {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
}

export default function ProfileTab() {
    const { register, handleSubmit, setValue, formState: { errors, isSubmitting } } = useForm<ProfileData>();
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [telegramChatId, setTelegramChatId] = useState<string | null>(null);
    const [telegramLoading, setTelegramLoading] = useState(true);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const res = await accountService.getProfile();
                const data = res.data;
                setValue('firstName', data.firstName);
                setValue('lastName', data.lastName);
                setValue('email', data.email);
                setValue('phoneNumber', data.phoneNumber);
            } catch (error) {
                console.error("Failed to load profile", error);
            } finally {
                setLoading(false);
            }
        };
        const fetchTelegram = async () => {
            try {
                const res = await landlordApi.get('api/settings/notifications');
                setTelegramChatId(res.data?.data?.telegramChatId || null);
            } catch {
                setTelegramChatId(null);
            } finally {
                setTelegramLoading(false);
            }
        };
        fetchProfile();
        fetchTelegram();
    }, [setValue]);

    const onSubmit = async (data: ProfileData) => {
        setMessage(null);
        try {
            await accountService.updateProfile({
                firstName: data.firstName,
                lastName: data.lastName,
                phoneNumber: data.phoneNumber,
                // Email is usually read-only
            });
            setMessage({ type: 'success', text: 'Profile updated successfully.' });
        } catch (error) {
            console.error("Update failed", error);
            setMessage({ type: 'error', text: 'Failed to update profile.' });
        }
    };

    if (loading) return <div className="p-4"><i className="fas fa-spinner fa-spin"></i> Loading...</div>;

    return (
        <>
        <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">Profile Settings</h3>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 max-w-xl">
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                    <div className="sm:col-span-3">
                        <label className="block text-sm font-medium text-gray-700">First name</label>
                        <div className="mt-1">
                            <input
                                type="text"
                                {...register('firstName', { required: 'First name is required' })}
                                className="shadow-sm focus:ring-green-500 focus:border-green-500 block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3 border"
                            />
                            {errors.firstName && <span className="text-red-500 text-xs">{errors.firstName.message}</span>}
                        </div>
                    </div>

                    <div className="sm:col-span-3">
                        <label className="block text-sm font-medium text-gray-700">Last name</label>
                        <div className="mt-1">
                            <input
                                type="text"
                                {...register('lastName', { required: 'Last name is required' })}
                                className="shadow-sm focus:ring-green-500 focus:border-green-500 block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3 border"
                            />
                            {errors.lastName && <span className="text-red-500 text-xs">{errors.lastName.message}</span>}
                        </div>
                    </div>

                    <div className="sm:col-span-6">
                        <label className="block text-sm font-medium text-gray-700">Email address</label>
                        <div className="mt-1">
                            <input
                                type="email"
                                disabled
                                {...register('email')}
                                className="bg-gray-50 shadow-sm block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3 border text-gray-500 cursor-not-allowed"
                            />
                            <p className="mt-1 text-xs text-gray-500">Email cannot be changed as it is your unique identifier.</p>
                        </div>
                    </div>

                    <div className="sm:col-span-6">
                        <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                        <div className="mt-1">
                            <input
                                type="tel"
                                {...register('phoneNumber')}
                                className="shadow-sm focus:ring-green-500 focus:border-green-500 block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3 border"
                            />
                        </div>
                    </div>
                </div>

                {message && (
                    <div className={`p-4 rounded-md ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                        {message.text}
                    </div>
                )}

                <div className="flex justify-end">
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                    >
                        {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </form>
        </div>

        <div className="bg-white shadow rounded-lg p-6 mt-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                <i className="fab fa-telegram-plane text-blue-500 mr-2"></i>
                Telegram Notifications
            </h3>
            <p className="text-sm text-gray-500 mb-4">
                Get instant alerts when tenants sign documents, booking requests arrive, or certificates are about to expire.
            </p>
            {telegramLoading ? (
                <p className="text-sm text-gray-400"><i className="fas fa-spinner fa-spin"></i> Loading...</p>
            ) : telegramChatId ? (
                <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div>
                        <p className="text-sm font-medium text-green-800">✓ Connected</p>
                        <p className="text-xs text-green-600">You'll receive instant notifications here.</p>
                    </div>
                    <button
                        onClick={async () => {
                            try {
                                await landlordApi.post('api/settings/notifications', { telegramChatId: null });
                                setTelegramChatId(null);
                            } catch { alert('Failed to disconnect. Try again.'); }
                        }}
                        className="text-sm text-red-600 hover:text-red-800"
                    >
                        Disconnect
                    </button>
                </div>
            ) : (
                <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-sm text-gray-600">Not connected</p>
                    <a
                        href={`https://t.me/TheCE_bot?start=${getTenantId() || ''}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition"
                    >
                        <i className="fab fa-telegram-plane mr-2"></i>
                        Connect Telegram
                    </a>
                </div>
            )}
        </div>
        </>
    );
}
